import { useState, useMemo, useEffect } from 'react';
import { useWatchResource } from '@common/hooks/useWatchResource';
import { useWatchMetrics } from '@common/hooks/useWatchMetrics';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DBSessionThunks, SessionReducerActions } from '@common/reducers/DBServiceThunks';
import { backendApiEndpoints } from '@common/reducers/backend-api/backend-api';
import { 
  ClusterMetrics, 
  NodeMetricsMap, 
  PodMetricsMap,
  NAMESPACE,
  SessionStats
} from '../utils/types';
import { 
  parseMemory, 
  formatMemory, 
  getNodeType 
} from '../utils/helpers';
import { loadAllSessions, toggleNode as toggleNodeUtil } from '../utils/clusterActions';

// Create a new thunk to fetch all sessions with pagination
export const fetchAllSessions = createAsyncThunk(
  'sessions/fetchAll',
  async (_, { dispatch }) => {
    // Clear existing sessions first
    dispatch(SessionReducerActions.clearState());
    
    const PAGE_SIZE = 1000;
    let skip = 0;
    let hasMore = true;
    let totalFetched = 0;
    
    try {
      while (hasMore) {
        // Fetch a page of sessions using the API
        const response = await dispatch(
          backendApiEndpoints.getAllSessions.initiate({
            limit: PAGE_SIZE,
            skip,
            sortBy: 'updatedAt',
            sortOrder: -1
          })
        ).unwrap();
        
        // Process the response
        const { total, data } = response;
        
        if (data && data.length > 0) {
          // Add sessions to the Redux store using the session actions
          dispatch(SessionReducerActions.addMany(data));
          totalFetched += data.length;
          
          // Check if we need to continue
          skip += PAGE_SIZE;
          hasMore = skip < total;
        } else {
          hasMore = false;
        }
      }
      
      return { totalFetched };
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }
);

export const useClusterData = () => {
  const dispatch = useAppDispatch();
  
  // Fetch Kubernetes resources using Watch API
  const { resources: nodes, isLoading: isLoadingNodes, error: nodesError } = useWatchResource('nodes');
  const { resources: pods, isLoading: isLoadingPods, error: podsError } = useWatchResource('pods', NAMESPACE);
  const { metrics: nodeMetricsData, isLoading: isLoadingNodeMetrics, error: nodeMetricsError } = useWatchMetrics('nodes');
  const { metrics: podMetricsData, isLoading: isLoadingPodMetrics, error: podMetricsError } = useWatchMetrics('pods', NAMESPACE);
  
  // Session state
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const [paginationStatus, setPaginationStatus] = useState({ total: 0 });
  
  // Get sessions from the redux store
  const sessions = useAppSelector(state => state.DBSessionReducer.entities);
  
  // UI state
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [nodeMetrics, setNodeMetrics] = useState<NodeMetricsMap>({});
  const [podMetrics, setPodMetrics] = useState<PodMetricsMap>({});
  const [showGatewayNodes, setShowGatewayNodes] = useState<boolean>(true);
  const [showNormalNodes, setShowNormalNodes] = useState<boolean>(true);

  // Process node metrics whenever they change
  useEffect(() => {
    if (!nodeMetricsData?.items) return;
    
    const nodeMetricsMap: NodeMetricsMap = {};
    
    // Process each node's metrics
    nodeMetricsData.items.forEach((item: any) => {
      const nodeName = item.metadata.name;
      
      const cpuUsage = item.usage?.cpu || '0';
      const memoryUsage = item.usage?.memory || '0';
      
      // Find corresponding node to get capacity
      const node = nodes.find(n => n.metadata.name === nodeName);
      const cpuCapacity = node?.status?.capacity?.cpu || '1';
      const memoryCapacity = node?.status?.capacity?.memory || '1Gi';
      
      // Parse memory values
      const memoryUsageBytes = parseMemory(memoryUsage);
      const memoryCapacityBytes = parseMemory(memoryCapacity);
      const memoryUsagePercentage = (memoryUsageBytes / memoryCapacityBytes) * 100;
      
      // Parse CPU values (simplified, actual CPU calculation would be more complex)
      const cpuUsageValue = parseInt(cpuUsage.replace(/[^0-9]/g, ''));
      const cpuCapacityValue = parseInt(cpuCapacity.replace(/[^0-9]/g, ''));
      const cpuUsagePercentage = (cpuUsageValue / cpuCapacityValue) * 100;
      
      nodeMetricsMap[nodeName] = {
        memory: {
          usage: memoryUsage,
          capacity: memoryCapacity,
          usagePercentage: memoryUsagePercentage
        },
        cpu: {
          usage: cpuUsage,
          capacity: cpuCapacity,
          usagePercentage: cpuUsagePercentage
        }
      };
    });
    
    setNodeMetrics(nodeMetricsMap);
  }, [nodeMetricsData, nodes]);
  
  // Process pod metrics whenever they change
  useEffect(() => {
    if (!podMetricsData?.items) return;
    
    const podMetricsMap: PodMetricsMap = {};
    
    // Process each pod's metrics
    podMetricsData.items.forEach((item: any) => {
      const podName = item.metadata.name;
      const namespace = item.metadata.namespace;
      
      // Sum memory across all containers
      let totalMemoryUsage = 0;
      let memoryUsageString = '0';
      
      if (item.containers && item.containers.length > 0) {
        item.containers.forEach((container: any) => {
          if (container.usage && container.usage.memory) {
            totalMemoryUsage += parseMemory(container.usage.memory);
          }
        });
        
        memoryUsageString = item.containers[0].usage?.memory || '0';
      }
      
      podMetricsMap[podName] = {
        name: podName,
        namespace: namespace,
        memory: {
          usage: memoryUsageString,
          usageBytes: totalMemoryUsage,
          usageFormatted: (totalMemoryUsage / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
        },
        cpu: {
          usage: item.containers && item.containers.length > 0 ? 
            item.containers[0].usage?.cpu || '0' : '0'
        }
      };
    });
    
    setPodMetrics(podMetricsMap);
  }, [podMetricsData]);
  
  // Filter and group pods by node based on node type filters
  const nodePodsMap = useMemo(() => {
    const map = new Map<string, { nodeInfo: any, pods: any[], nodeType: 'gateway' | 'normal' }>();
    
    if (!nodes.length || !pods.length) return map;
    
    // Initialize map with all nodes that match the active filters
    nodes.forEach(node => {
      const nodeType = getNodeType(node);
      
      // Skip nodes that don't match our filters
      if ((nodeType === 'gateway' && !showGatewayNodes) || 
          (nodeType === 'normal' && !showNormalNodes)) {
        return;
      }
      
      map.set(node.metadata.name, {
        nodeInfo: node,
        pods: [],
        nodeType
      });
    });
    
    // Add pods to their respective nodes (if the node is in our filtered map)
    pods.forEach(pod => {
      const nodeName = pod.spec?.nodeName;
      if (nodeName && map.has(nodeName)) {
        const nodeData = map.get(nodeName);
        if (nodeData) {
          nodeData.pods.push(pod);
          map.set(nodeName, nodeData);
        }
      }
    });
    
    return map;
  }, [nodes, pods, showGatewayNodes, showNormalNodes]);
  
  // Calculate cluster metrics
  const clusterMetrics = useMemo((): ClusterMetrics => {
    // Filter nodes based on selected types
    const filteredNodes = nodes.filter(node => {
      const nodeType = getNodeType(node);
      return (nodeType === 'gateway' && showGatewayNodes) || 
             (nodeType === 'normal' && showNormalNodes);
    });
    
    // Filter pods that belong to the filtered nodes
    const filteredPods = pods.filter(pod => {
      const nodeName = pod.spec?.nodeName;
      return filteredNodes.some(node => node.metadata.name === nodeName);
    });
    
    const totalNodes = filteredNodes.length;
    const totalPods = filteredPods.length;
    const runningPods = filteredPods.filter(pod => 
      pod.status?.phase === 'Running'
    ).length;
    
    // Calculate overall memory utilization for the filtered nodes
    let totalMemoryCapacity = 0;
    let totalMemoryUsage = 0;
    
    filteredNodes.forEach(node => {
      const nodeName = node.metadata.name;
      const nodeMetric = nodeMetrics[nodeName];
      
      if (nodeMetric) {
        totalMemoryCapacity += parseMemory(nodeMetric.memory.capacity);
        totalMemoryUsage += parseMemory(nodeMetric.memory.usage);
      }
    });
    
    const memoryUtilizationPercentage = totalMemoryCapacity > 0 
      ? (totalMemoryUsage / totalMemoryCapacity) * 100 
      : 0;
    
    return {
      totalNodes,
      totalPods,
      runningPods,
      memoryUtilization: {
        capacityFormatted: formatMemory(totalMemoryCapacity),
        usageFormatted: formatMemory(totalMemoryUsage),
        percentage: memoryUtilizationPercentage,
      }
    };
  }, [nodes, pods, nodeMetrics, showGatewayNodes, showNormalNodes]);

  // Enhanced session statistics with three categories
  const sessionStats = useMemo((): SessionStats => {
    const sessionArray = Object.values(sessions);
    const total = sessionArray.length;
    
    // Get all existing pod IDs from the pods list
    const existingPodIds = new Set(pods.map(pod => pod.metadata.name));
    
    // Categorize sessions
    const noPod = sessionArray.filter(session => !session.podId).length;
    const missingPod = sessionArray.filter(session => session.podId && !existingPodIds.has(session.podId)).length;
    const activeInPod = sessionArray.filter(session => session.podId && existingPodIds.has(session.podId)).length;
    
    return { 
      total, 
      noPod, 
      missingPod, 
      activeInPod 
    };
  }, [sessions, pods]);

  // Enhanced pod session map that only maps active pods
  const podSessionMap = useMemo(() => {
    const map = new Map<string, any[]>();
    
    // Use sessions from redux store
    const sessionArray = Object.values(sessions);
    if (!sessionArray.length) return map;
    
    // Get all existing pod IDs for quick lookup
    const existingPodIds = new Set(pods.map(pod => pod.metadata.name));
    
    // Only map sessions to pods that exist
    sessionArray.forEach(session => {
      if (session.podId && existingPodIds.has(session.podId)) {
        const podSessions = map.get(session.podId) || [];
        podSessions.push(session);
        map.set(session.podId, podSessions);
      }
    });
    
    return map;
  }, [sessions, pods]);

  const isLoading = isLoadingNodes || isLoadingPods || isLoadingNodeMetrics || isLoadingPodMetrics;
  const error = nodesError || podsError || nodeMetricsError || podMetricsError || sessionError;

  // Wrapper functions for our utility functions
  const handleLoadAllSessions = async () => {
    await loadAllSessions(
      dispatch, 
      setIsLoadingSessions, 
      setSessionError, 
      setPaginationStatus, 
      sessions
    );
  };

  const handleToggleNode = (nodeName: string) => {
    toggleNodeUtil(nodeName, expandedNodes, setExpandedNodes);
  };

  return {
    // Resources
    nodes,
    pods,
    
    // Metrics
    nodeMetrics,
    podMetrics,
    
    // Maps and computed data
    nodePodsMap,
    podSessionMap,
    clusterMetrics,
    sessionStats,
    
    // Sessions data
    sessions: Object.values(sessions),
    
    // UI state
    expandedNodes,
    showGatewayNodes,
    showNormalNodes,
    
    // Loading states
    isLoading,
    isLoadingSessions,
    error,
    
    // Functions
    loadAllSessions: handleLoadAllSessions,
    toggleNode: handleToggleNode,
    setShowGatewayNodes,
    setShowNormalNodes
  };
}; 